class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (!cartLink) return;

    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.setAttribute('aria-controls', this.id || 'CartDrawer');
    cartLink.setAttribute('aria-expanded', 'false');

    if (cartLink.dataset.cartDrawerBound === 'true') return;
    cartLink.dataset.cartDrawerBound = 'true';

    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (['SPACE', 'ENTER'].includes(event.code.toUpperCase())) {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  setHeaderCartIconExpanded(isExpanded) {
    const cartLink = document.querySelector('#cart-icon-bubble');
    if (cartLink) cartLink.setAttribute('aria-expanded', isExpanded ? 'true' : 'false');
  }

  open(triggeredBy) {
    if (this.classList.contains('active')) return;
    this.setHeaderCartIconExpanded(true);
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        trapFocus(containerToTrapFocusOn, focusElement);
      },
      { once: true },
    );

    document.body.classList.add('overflow-hidden');

    // cart-drawer-items is a CartItems subclass that extends createViewEventElement.
    // Its `view-event-trigger="manual"` skips auto-dispatch on connect; we fire
    // it here when the drawer opens, with `context: 'dialog'` from the payload attribute.
    this.querySelector('cart-drawer-items')?.dispatchViewEvent?.();
  }

  close() {
    this.classList.remove('active');
    this.setHeaderCartIconExpanded(false);
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const drawerInner = this.querySelector('.drawer__inner');
    if (!drawerInner || !parsedState?.sections || typeof parsedState.sections !== 'object') {
      console.error('Cart drawer response is missing sections.');
      return false;
    }

    // Shopify should return the requested drawer section. If an upstream
    // response contains unrelated keys only, keep the current drawer intact
    // instead of opening stale contents as if the add succeeded.
    if (typeof parsedState.sections['cart-drawer'] !== 'string') {
      console.error('Cart drawer response is missing the drawer section.');
      return false;
    }

    const sections = this.getSectionsToRender().map((section) => ({
      id: section.id,
      element: section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id),
      source: typeof parsedState.sections[section.id] === 'string'
        ? this.getSectionDOM(parsedState.sections[section.id], section.selector)
        : null,
    }));
    const drawerSection = sections.find((section) => section.id === 'cart-drawer');
    if (
      !drawerSection?.element ||
      !drawerSection.source?.querySelector('.drawer__inner') ||
      !drawerSection.source.querySelector('#CartDrawer-Overlay')
    ) return false;

    this.classList.toggle('is-empty',
      drawerSection.source.closest('cart-drawer')?.classList.contains('is-empty') || false);
    this.productId = parsedState.id;
    sections.forEach(({ element, source }) => {
      if (element && source) element.innerHTML = source.innerHTML;
    });

    this.setHeaderCartIconAccessibility();

    setTimeout(() => {
      this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
      this.open();
    });
    return true;
  }

  async refreshContents() {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 8000);
    try {
      const sections = this.getSectionsToRender().map((section) => section.id).join(',');
      const response = await fetch(`${window.routes.cart_url}?sections=${encodeURIComponent(sections)}`, {
        signal: controller.signal,
        headers: { Accept: 'application/json' },
      });
      if (!response.ok) return false;
      return this.renderContents({ sections: await response.json() });
    } finally {
      clearTimeout(timeoutId);
    }
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

if (!customElements.get('cart-drawer')) {
  customElements.define('cart-drawer', CartDrawer);
}

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

if (!customElements.get('cart-drawer-items')) {
  customElements.define('cart-drawer-items', CartDrawerItems);
}

